import React from 'react';
import {
  Box, Typography, Button, Paper, IconButton, Autocomplete, TextField,
  Tooltip, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// ---------------------------------------------------------------------------
// ScopeEditor — multi-clause allowed_actions builder.
//
// Renders a list of <ScopeClause> entries the partner is building. The
// underlying wire shape is `{ allowed_actions: string[], data_scope: object }[]`
// (see platform/common-core/.../ScopeClause.java). The TokenScope-matching
// rule (access-profile §4.4): an action is permitted if ANY clause's
// allowed_actions covers it AND the clause's data_scope matches the row.
//
// v1 scope (kept simple intentionally — handoff doc § Step 5):
//   - Per clause: allowed_actions multi-select with the standard verb
//     catalog as suggestions + freeSolo for custom partner verbs
//   - Per clause: data_scope is fixed to {} (no UI). Partners needing
//     row-level filters use the POST /v1/app-contexts/{ctx}/profiles
//     endpoint directly. Surfaced via the Alert below.
//   - Add Clause / Remove Clause controls
//
// Future MR #6 work — extracted to @vectros-ai/react — will add a
// data_scope key/value picker. For now, the simpler shape covers the
// common "agent with admin:keys + read on everything" pattern.
// ---------------------------------------------------------------------------

// Standard verb catalog — access-profile/design.md §4.5. Surfaced as
// Autocomplete suggestions; partners can also type custom verbs.
const STANDARD_VERB_CATALOG = [
  '*',
  'read',
  'write',
  'delete',
  'admin:keys',
  'admin:profiles',
  'logs:r',
];

/**
 * Build a fresh empty clause. allowed_actions is an empty list (the
 * editor will validate at submit time); data_scope is `{}` (applies to
 * all data within the tenant per access-profile §4.4).
 */
export function emptyClause() {
  return { allowed_actions: [], data_scope: {} };
}

/**
 * Validate the supplied clause list. Returns null if valid, or a string
 * error message identifying the first problem found. Mirrors backend
 * `ScopeClause.validate` logic + the AccessProfile "at least one clause"
 * requirement.
 */
export function validateClauses(clauses) {
  if (!clauses || clauses.length === 0) {
    return 'At least one scope clause is required';
  }
  for (let i = 0; i < clauses.length; i++) {
    const c = clauses[i];
    if (!c.allowed_actions || c.allowed_actions.length === 0) {
      return `Clause ${i + 1}: at least one allowed action is required`;
    }
    for (let j = 0; j < c.allowed_actions.length; j++) {
      const a = c.allowed_actions[j];
      if (!a || typeof a !== 'string' || !a.trim()) {
        return `Clause ${i + 1}, action ${j + 1}: must be a non-blank string`;
      }
    }
  }
  return null;
}

/**
 * Controlled multi-clause editor.
 *
 * @param {object} props
 * @param {Array<{allowed_actions: string[], data_scope: object}>} props.value
 *   Current list of clauses. Caller owns the state.
 * @param {(next: Array) => void} props.onChange  Replaces the whole list.
 */
export default function ScopeEditor({ value, onChange }) {
  const clauses = value || [];

  const updateClause = (idx, patch) => {
    const next = clauses.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    onChange(next);
  };

  const addClause = () => {
    onChange([...clauses, emptyClause()]);
  };

  const removeClause = (idx) => {
    onChange(clauses.filter((_, i) => i !== idx));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Alert severity="info" icon={<InfoOutlinedIcon />}>
        <Typography variant="caption">
          Pick one or more verbs per clause. <code>*</code> grants all actions
          (OWNER default). Data-row filters (e.g. "only org_engineering rows")
          aren't editable here — use <code>POST /v1/app-contexts/{'{ctx}'}/profiles</code>
          for those.
        </Typography>
      </Alert>

      {clauses.length === 0 && (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            textAlign: 'center',
            color: 'text.secondary',
            borderStyle: 'dashed',
          }}
        >
          <Typography variant="body2">No clauses yet — click "Add clause" below</Typography>
        </Paper>
      )}

      {clauses.map((clause, idx) => (
        <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5,
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Clause {idx + 1}
            </Typography>
            <Tooltip title="Remove clause">
              <IconButton size="small" aria-label="Remove clause" onClick={() => removeClause(idx)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Autocomplete
            multiple
            freeSolo
            options={STANDARD_VERB_CATALOG}
            value={clause.allowed_actions || []}
            onChange={(_, v) => updateClause(idx, { allowed_actions: v })}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Allowed actions"
                placeholder="Pick from catalog or type a custom verb"
                size="small"
              />
            )}
          />
        </Paper>
      ))}

      <Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={addClause}
          sx={{ textTransform: 'none' }}
        >
          Add clause
        </Button>
      </Box>
    </Box>
  );
}
